import type { AuthContextValue } from '@/auth/AuthProvider';

import {
  KUDU_AUTH_TOKEN_REQUEST,
  KUDU_AUTH_TOKEN_RESPONSE,
  KUDU_REPLAY_QUEUE,
  KUDU_SYNC_CHANNEL,
  type AuthTokenRequestMessage,
  type AuthTokenResponseMessage,
  type ReplayQueueMessage,
  type SyncStatusMessage,
} from './syncMessages';

function answerTryLater(event: MessageEvent<unknown>) {
  const message = event.data as Partial<AuthTokenRequestMessage>;

  if (message.type === KUDU_AUTH_TOKEN_REQUEST && message.requestId) {
    const response: AuthTokenResponseMessage = {
      reason: 'token-error',
      requestId: message.requestId,
      type: KUDU_AUTH_TOKEN_RESPONSE,
    };

    event.ports[0]?.postMessage(response);
  }
}

// A worker's messages reach a page once its scripts have run, and the bridge below
// is installed only after a render. A token request in between would find nobody
// listening, and the worker would wait out its 5 seconds for an answer, with the
// page's own ask behind it. So from the moment this file loads the page answers
// "try later", until the bridge takes over.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', answerTryLater);
}

function postReplayQueueMessage() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const message: ReplayQueueMessage = { type: KUDU_REPLAY_QUEUE };

  try {
    navigator.serviceWorker.controller?.postMessage(message);
  } catch {
    // A worker on its way out can refuse a message. This runs as the page renders,
    // where that would take the page with it, and the next ask reaches its successor.
  }
}

// The page asks the worker to send its queue, at once and on every chance after,
// whether or not the browser has a sync of its own to come. A page cannot count on
// one: the browser's next try can be minutes off, it stops trying after a few, and
// the site's setting can turn it off. Nothing else would send the answer, which
// the page has called saved. Asking as well does no harm, as the worker runs one
// replay at a time.
//
// Only for a signed-in page: told "signed out", the worker drops the queue.
export function installManualReplayListeners() {
  function replayWhenOnline() {
    if (navigator.onLine) {
      postReplayQueueMessage();
    }
  }

  function replayWhenVisible() {
    if (document.visibilityState === 'visible') {
      replayWhenOnline();
    }
  }

  replayWhenOnline();

  window.addEventListener('online', replayWhenOnline);
  window.addEventListener('focus', replayWhenOnline);
  document.addEventListener('visibilitychange', replayWhenVisible);

  return () => {
    window.removeEventListener('online', replayWhenOnline);
    window.removeEventListener('focus', replayWhenOnline);
    document.removeEventListener('visibilitychange', replayWhenVisible);
  };
}

export function installServiceWorkerMessageBridge(auth: AuthContextValue) {
  if (!('serviceWorker' in navigator)) {
    return () => {};
  }

  navigator.serviceWorker.removeEventListener('message', answerTryLater);

  async function handleAuthTokenRequest(
    message: AuthTokenRequestMessage,
    port?: MessagePort,
  ) {
    let response: AuthTokenResponseMessage;

    try {
      // While the sign-in is being restored there is no user yet, which is not
      // being signed out: on "signed-out" the worker drops every queued answer.
      if (auth.loading) {
        throw new Error('The sign-in is still being restored.');
      }

      const token = await auth.getIdToken(true);

      response = token
        ? {
            requestId: message.requestId,
            token,
            type: KUDU_AUTH_TOKEN_RESPONSE,
          }
        : {
            reason: 'signed-out',
            requestId: message.requestId,
            type: KUDU_AUTH_TOKEN_RESPONSE,
          };
    } catch {
      response = {
        reason: 'token-error',
        requestId: message.requestId,
        type: KUDU_AUTH_TOKEN_RESPONSE,
      };
    }

    port?.postMessage(response);
  }

  function handleMessage(event: MessageEvent<unknown>) {
    const message = event.data as Partial<AuthTokenRequestMessage | SyncStatusMessage>;

    if (message.type === KUDU_AUTH_TOKEN_REQUEST && message.requestId) {
      void handleAuthTokenRequest(
        message as AuthTokenRequestMessage,
        event.ports[0],
      );
      return;
    }

    if (message.type === 'sync-status') {
      window.dispatchEvent(
        new CustomEvent(KUDU_SYNC_CHANNEL, {
          detail: message,
        }),
      );
    }
  }

  navigator.serviceWorker.addEventListener('message', handleMessage);
  return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
}

